import { useState, useEffect } from "react";
import { Modal, Input, Alert } from "antd";
import { MailOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import { sendInvoice } from "../services/invoiceService";

/**
 * Email one document to a customer.
 *
 * The same modal wherever a document can be sent — the wallet ledger, the
 * subscription history, the invoice list — because the question is identical
 * every time: which address, and confirm which document.
 *
 * It takes a `send_endpoint` rather than a type and an id, so a document kind
 * this file has never heard of is sendable the day the API returns one.
 */

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SendInvoiceModal = ({
  open,
  invoice,          // { invoice_number, amount, amountText?, title, send_endpoint }
  userId,
  defaultEmail = "",
  onClose,
  onSent,           // called after a successful send, so the row can refresh
}) => {
  const [email, setEmail] = useState(defaultEmail);
  const [sending, setSending] = useState(false);
  const [touched, setTouched] = useState(false);

  /* Re-seeded each time it opens: the modal is reused across rows, and a
     previous recipient left in the box is how the wrong person gets an
     invoice. */
  useEffect(() => {
    if (open) {
      setEmail(defaultEmail || "");
      setTouched(false);
    }
  }, [open, defaultEmail]);

  const trimmed = email.trim();
  const invalid = touched && trimmed !== "" && !EMAIL.test(trimmed);

  const submit = async () => {
    if (sending || invalid) return;
    setSending(true);
    try {
      const { message } = await sendInvoice(invoice.send_endpoint, userId, trimmed);
      toast.success(message);
      onSent?.();
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSending(false);
    }
  };

  const money = (n) => {
    const v = Number(n);
    if (!Number.isFinite(v)) return "—";
    const paise = Math.abs(v % 1) > 0;
    return `₹${v.toLocaleString("en-IN", {
      minimumFractionDigits: paise ? 2 : 0,
      maximumFractionDigits: 2,
    })}`;
  };

  return (
    <Modal
      open={open}
      onCancel={sending ? undefined : onClose}
      onOk={submit}
      okText={sending ? "Sending…" : "Send"}
      /* Disabled while in flight, not just spinning: building the PDF and
         handing it to the mail server takes a few seconds, and a second click
         in that window sends the customer two copies. */
      okButtonProps={{ loading: sending, disabled: sending || invalid, icon: <MailOutlined /> }}
      cancelButtonProps={{ disabled: sending }}
      maskClosable={!sending}
      closable={!sending}
      width={460}
      title={<span style={{ fontWeight: 700, fontSize: 15 }}>Send invoice</span>}
    >
      {/* What is being sent, stated and not editable — the address is the only
          decision here. */}
      <div style={{
        background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10,
        padding: "12px 14px", marginBottom: 16,
      }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>
          {invoice?.invoice_number || "—"}
        </div>
        {invoice?.title && (
          <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>{invoice.title}</div>
        )}
        {/* Not every ledger states the document's rupee total — the AI credit
            ledger counts credits, and the price only exists inside the receipt.
            Such a caller passes `amountText` and it is printed verbatim; a
            caller with neither shows no figure rather than a ₹ made up from
            the wrong unit. */}
        {(invoice?.amountText
          || (invoice?.amount !== null && invoice?.amount !== undefined)) && (
          <div style={{ fontSize: 15, fontWeight: 800, color: "#0f172a", marginTop: 6, fontVariantNumeric: "tabular-nums" }}>
            {invoice.amountText || money(invoice.amount)}
          </div>
        )}
      </div>

      <label style={{ fontSize: 12, fontWeight: 700, color: "#374151", display: "block", marginBottom: 6 }}>
        To email
      </label>
      <Input
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        onBlur={() => setTouched(true)}
        onPressEnter={submit}
        disabled={sending}
        status={invalid ? "error" : ""}
        placeholder="customer@example.com"
        prefix={<MailOutlined style={{ color: "#94a3b8" }} />}
      />
      {invalid && (
        <div style={{ fontSize: 12, color: "#dc2626", marginTop: 6 }}>
          That does not look like an email address.
        </div>
      )}
      {/* Blank is a real choice, so it is explained rather than blocked. */}
      {!trimmed && (
        <div style={{ fontSize: 11.5, color: "#94a3b8", marginTop: 6 }}>
          Leave empty to send to the customer&apos;s registered address.
        </div>
      )}

      {sending && (
        <Alert
          type="info"
          showIcon
          style={{ marginTop: 14 }}
          message="Building the PDF and sending — this takes a few seconds."
        />
      )}
    </Modal>
  );
};

export default SendInvoiceModal;
